import React, { useState } from 'react';
import './DeleteAccount.css';

const API_URL = 'https://api.basniconnect.online/api/v1/account-deletion-request';

type Step = 'form' | 'received' | 'already_submitted';

const DeleteAccount: React.FC = () => {
  const [step, setStep] = useState<Step>('form');
  const [email, setEmail] = useState('');
  const [reason, setReason] = useState('');
  const [confirm, setConfirm] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState('');

  const validate = () => {
    const e: Record<string, string> = {};
    if (!email.trim()) e.email = 'Email is required.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) e.email = 'Enter a valid email address.';
    if (!confirm) e.confirm = 'You must confirm to proceed.';
    return e;
  };

  const handleSubmit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    const e = validate();
    if (Object.keys(e).length > 0) { setErrors(e); return; }
    setErrors({});
    setApiError('');
    setLoading(true);

    try {
      const body: Record<string, string> = { email: email.trim() };
      if (reason.trim()) body.reason = reason.trim();

      const res = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (res.status === 201) {
        setStep('received');
      } else if (res.status === 200) {
        setStep('already_submitted');
      } else {
        const data = await res.json().catch(() => ({}));
        setApiError(data.message || 'Something went wrong. Please try again.');
      }
    } catch {
      setApiError('Unable to connect. Please check your internet and try again.');
    } finally {
      setLoading(false);
    }
  };

  if (step === 'received' || step === 'already_submitted') {
    const alreadyDone = step === 'already_submitted';
    return (
      <div className="da-root">
        <header className="da-header">
          <div className="da-header-inner">
            <div className="da-logo">
              <img src="/app_logo.png" alt="BasniConnect" className="da-logo-img" />
              <span className="da-logo-text">BasniConnect</span>
            </div>
            <span className="da-badge da-badge--warn">Account Deletion</span>
          </div>
        </header>

        <div className="da-center">
          <div className="da-success-card">
            <div className={`da-success-icon ${alreadyDone ? 'da-success-icon--blue' : ''}`}>
              {alreadyDone ? '!' : '✓'}
            </div>
            <h2 className="da-success-title">
              {alreadyDone ? 'Already Submitted' : 'Request Received'}
            </h2>
            <p className="da-success-text">
              {alreadyDone
                ? <>A deletion request for <strong>{email}</strong> has already been submitted. Our team is processing it.</>
                : <>We have received your account deletion request for <strong>{email}</strong>. Our team will process it within <strong>30 days</strong>.</>
              }
            </p>
            <p className="da-success-note">
              Once deleted, all your data — including listings, announcements, and profile
              information — will be permanently removed and cannot be recovered.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="da-root">
      <header className="da-header">
        <div className="da-header-inner">
          <div className="da-logo">
            <img src="/app_logo.png" alt="BasniConnect" className="da-logo-img" />
            <span className="da-logo-text">BasniConnect</span>
          </div>
          <span className="da-badge da-badge--warn">Account Deletion</span>
        </div>
      </header>

      <div className="da-body">
        <div className="da-hero">
          <h1 className="da-title">Delete Your Account</h1>
          <p className="da-subtitle">
            We're sorry to see you go. Fill in the form below to submit an account
            deletion request. This action is permanent and cannot be undone.
          </p>
        </div>

        <div className="da-layout">
          <aside className="da-info-panel">
            <div className="da-info-card">
              <h3 className="da-info-heading">What gets deleted</h3>
              <ul className="da-info-list">
                {[
                  'Your profile and account credentials',
                  'All listings and announcements you posted',
                  'Your messages and contact history',
                  'Your saved preferences and settings',
                ].map((item) => (
                  <li key={item} className="da-info-item">
                    <span className="da-info-dot da-info-dot--red" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="da-info-card">
              <h3 className="da-info-heading">What to expect</h3>
              <ul className="da-info-list">
                {[
                  'Request processed within 30 days',
                  'Confirmation email sent to you',
                  'Data permanently deleted after confirmation',
                ].map((item) => (
                  <li key={item} className="da-info-item">
                    <span className="da-info-dot" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="da-info-card da-info-card--alt">
              <h3 className="da-info-heading">Need help instead?</h3>
              <p className="da-info-text">
                If you're having trouble with the app, we'd love to help before you go.
              </p>
              <a
                href="mailto:mohammedrazachandwala@gmail.com"
                className="da-contact-link"
              >
                mohammedrazachandwala@gmail.com
              </a>
            </div>
          </aside>

          <main className="da-form-panel">
            <form className="da-form" onSubmit={handleSubmit} noValidate>
              <div className="da-form-group">
                <label className="da-label" htmlFor="email">
                  Registered Email Address <span className="da-required">*</span>
                </label>
                <input
                  id="email"
                  type="email"
                  className={`da-input ${errors.email ? 'da-input--error' : ''}`}
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                />
                {errors.email && <p className="da-error">{errors.email}</p>}
              </div>

              <div className="da-form-group">
                <label className="da-label" htmlFor="reason">
                  Reason for Leaving <span className="da-optional">(optional)</span>
                </label>
                <textarea
                  id="reason"
                  className="da-textarea"
                  placeholder="Help us improve — tell us why you're deleting your account."
                  rows={4}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  disabled={loading}
                />
              </div>

              <div className={`da-checkbox-group ${errors.confirm ? 'da-checkbox-group--error' : ''}`}>
                <label className="da-checkbox-label">
                  <input
                    type="checkbox"
                    className="da-checkbox"
                    checked={confirm}
                    onChange={(e) => setConfirm(e.target.checked)}
                    disabled={loading}
                  />
                  <span className="da-checkbox-text">
                    I understand that deleting my account is <strong>permanent and irreversible</strong>.
                    All my data will be removed and cannot be recovered.
                  </span>
                </label>
                {errors.confirm && <p className="da-error">{errors.confirm}</p>}
              </div>

              {apiError && <p className="da-error da-error--banner">{apiError}</p>}

              <button type="submit" className="da-submit-btn" disabled={loading}>
                {loading ? 'Submitting…' : 'Submit Deletion Request'}
              </button>
            </form>
          </main>
        </div>
      </div>

      <footer className="da-footer">
        <div className="da-footer-inner">
          <div className="da-footer-logo">
            <img src="/app_logo.png" alt="BasniConnect" className="da-logo-img da-logo-img--sm" />
            <span className="da-logo-text">BasniConnect</span>
          </div>
          <p className="da-footer-text">© {new Date().getFullYear()} BasniConnect. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default DeleteAccount;
